import { useEffect } from "react";
import {
  type Control,
  type FieldPath,
  type FieldPathByValue,
  type FieldPathValue,
  type FieldValues,
  useWatch,
} from "react-hook-form";
import { CheckboxField } from "@/components/fields/checkbox-field";
import type { LocaleKey } from "@/i18n";
import { getPixelTrackingConsentLabel } from "@/i18n/pixel-tracking-consent";
import { cn } from "@/lib/utils";

export interface CdcExplicitConsentFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  text: string;
  /** Language for the fixed pixel-tracking copy, not the authored parent text. */
  locale?: LocaleKey;
  /** Off by default. Tracking remains optional even when the parent is required. */
  showTrackingConsent?: boolean;
  /** Boolean form field to bind when tracking is enabled. Do not preselect it. */
  trackingConsentName?: FieldPathByValue<TFieldValues, boolean | undefined>;
}
// Renders a CDC explicit consent checkbox field
export function CdcExplicitConsentField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  className,
  text,
  locale = "en-US",
  showTrackingConsent = false,
  trackingConsentName,
  ...htmlProps
}: Readonly<CdcExplicitConsentFieldProps<TFieldValues, TName>>) {
  const consent = useWatch({ control, name, exact: true });
  const trackingIsVisible = showTrackingConsent && consent === true;
  // Also catch resets that restore a hidden value without changing the parent.
  const trackingValue = useWatch({
    control,
    name: trackingConsentName ?? name,
    disabled: !trackingConsentName,
  });
  useEffect(() => {
    if (trackingConsentName && !trackingIsVisible) {
      control.unregister(trackingConsentName);
    }
  }, [control, trackingConsentName, trackingIsVisible, trackingValue]);

  if (showTrackingConsent && trackingConsentName) {
    return (
      <div className={cn("space-y-4", className)} {...htmlProps}>
        <CheckboxField control={control} name={name} label={text} />
        {trackingIsVisible ? (
          <CheckboxField
            control={control}
            name={trackingConsentName}
            label={getPixelTrackingConsentLabel(locale)}
            // RHF cannot infer a boolean default through the generic field path.
            controllerDefaultValue={
              false as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>
            }
            shouldUnregister
            className="ms-7"
          />
        ) : null}
      </div>
    );
  }
  return (
    <CheckboxField
      control={control}
      name={name}
      label={text}
      className={className}
      {...htmlProps}
    />
  );
}
